/**
 * PointsDisplay — compact header widget showing XP, level, streak.
 * Fetches from the points backend using the connected wallet address.
 * Animates briefly when XP increases.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pointsApi } from '../../services/api';
import { LightningIcon } from '../icons/WeatherIcons';

interface PointsDisplayProps {
    address?: string;
    compact?: boolean;
}

interface UserPoints {
    address: string;
    username: string | null;
    joined_at: string;
    total_points: number;
    level: number;
    current_streak: number;
    best_streak: number;
    streak_multiplier: number;
}

// Level thresholds
const LEVEL_NAMES = ['Rookie', 'Forecaster', 'Analyst', 'Expert', 'Master', 'Legend'];
function levelName(level: number) {
    return LEVEL_NAMES[Math.min(level - 1, LEVEL_NAMES.length - 1)] ?? 'Legend';
}

export function PointsDisplay({ address, compact = true }: PointsDisplayProps) {
    const prevPoints = useRef<number | null>(null);
    const [flash, setFlash] = useState(false);

    const { data, isLoading, isError } = useQuery<UserPoints>({
        queryKey: ['points-user', address],
        queryFn: () => pointsApi.getUserPoints(address!),
        enabled: !!address,
        staleTime: 30_000,
        retry: false,
    });

    // Animate when XP increases
    useEffect(() => {
        if (!data) return;
        if (prevPoints.current !== null && data.total_points > prevPoints.current) {
            setFlash(true);
            const t = setTimeout(() => setFlash(false), 800);
            return () => clearTimeout(t);
        }
        prevPoints.current = data.total_points;
    }, [data?.total_points]);

    if (!address || isError || isLoading || !data) {
        return null; // Don't show widget if no wallet connected or API down
    }

    if (compact) {
        return (
            <div className={`points-widget-compact${flash ? ' xp-flash' : ''}`} title={`Level ${data.level} ${levelName(data.level)}`}>
                <span className="pw-icon">✨</span>
                <span className="pw-value">{data.total_points.toLocaleString()}</span>
                {data.current_streak > 1 && (
                    <span className="pw-streak" title={`${data.current_streak}-day streak`}>
                        <LightningIcon size={12} />{data.current_streak}
                    </span>
                )}
                <style>{`
                    .points-widget-compact {
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        background: rgba(255,255,255,0.07);
                        border: 1px solid rgba(255,255,255,0.12);
                        border-radius: 20px;
                        padding: 4px 10px;
                        font-size: 13px;
                        font-weight: 600;
                        color: #e2e8f0;
                        transition: background 0.3s;
                        cursor: default;
                        user-select: none;
                    }
                    .points-widget-compact.xp-flash {
                        background: rgba(255, 215, 0, 0.25);
                        border-color: #ffd700;
                        animation: xp-pulse 0.8s ease-out;
                    }
                    @keyframes xp-pulse {
                        0% { box-shadow: 0 0 0 0 rgba(255,215,0,0.6); }
                        70% { box-shadow: 0 0 0 10px rgba(255,215,0,0); }
                        100% { box-shadow: 0 0 0 0 rgba(255,215,0,0); }
                    }
                    .pw-icon { font-size: 14px; }
                    .pw-value { letter-spacing: 0.02em; }
                    .pw-streak { font-size: 12px; opacity: 0.85; }
                `}</style>
            </div>
        );
    }

    // Full card variant
    const xpForNext = data.level * 500;
    const xpProgress = Math.min(100, (data.total_points % xpForNext) / xpForNext * 100);

    return (
        <div className={`points-widget-card${flash ? ' xp-flash' : ''}`}>
            <div className="pwc-header">
                <span className="pwc-level">Lv.{data.level} {levelName(data.level)}</span>
                <span className="pwc-xp">✨ {data.total_points.toLocaleString()} XP</span>
            </div>
            <div className="pwc-bar-bg">
                <div className="pwc-bar-fill" style={{ width: `${xpProgress}%` }} />
            </div>
            <div className="pwc-footer">
                <span><LightningIcon size={12} /> Streak: {data.current_streak}d</span>
                <span>{xpProgress.toFixed(0)}% to Lv.{data.level + 1}</span>
            </div>
            <style>{`
                .points-widget-card {
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 12px;
                    padding: 12px 14px;
                    min-width: 200px;
                    transition: background 0.3s;
                }
                .points-widget-card.xp-flash {
                    background: rgba(255,215,0,0.12);
                    border-color: #ffd700;
                    animation: xp-pulse 0.8s ease-out;
                }
                @keyframes xp-pulse {
                    0% { box-shadow: 0 0 0 0 rgba(255,215,0,0.6); }
                    70% { box-shadow: 0 0 0 10px rgba(255,215,0,0); }
                    100% { box-shadow: 0 0 0 0 rgba(255,215,0,0); }
                }
                .pwc-header { display: flex; justify-content: space-between; font-size: 13px; color: #e2e8f0; margin-bottom: 8px; }
                .pwc-level { font-weight: 700; color: #ffd700; }
                .pwc-xp { font-weight: 600; }
                .pwc-bar-bg { background: rgba(255,255,255,0.1); border-radius: 4px; height: 6px; overflow: hidden; margin-bottom: 6px; }
                .pwc-bar-fill { height: 100%; background: linear-gradient(90deg, #ffd700, #ff8c00); border-radius: 4px; transition: width 0.5s ease; }
                .pwc-footer { display: flex; justify-content: space-between; font-size: 11px; color: rgba(255,255,255,0.5); }
            `}</style>
        </div>
    );
}

export default PointsDisplay;
